import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TrendingUp, Package, Users, DollarSign, AlertCircle, Filter, Search } from 'lucide-react';
import { fetchDashboardStats, fetchDeliveries } from '../services/api';

const Dashboard = () => {
  const [stats, setStats] = useState({
    totalOrders: 0,
    pendingOrders: 0,
    totalRevenue: 0,
    totalCustomers: 0,
    lowStockItems: 0
  });

  const [deliveries, setDeliveries] = useState([]);
  const [filteredDeliveries, setFilteredDeliveries] = useState([]);
  const [statusFilter, setStatusFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  useEffect(() => {
    filterDeliveries();
  }, [deliveries, statusFilter]);

  const loadDashboardData = async () => {
    try {
      const [statsData, deliveriesData] = await Promise.all([
        fetchDashboardStats(),
        fetchDeliveries()
      ]);
      setStats(statsData.stats);
      setDeliveries(deliveriesData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterDeliveries = () => {
    let result = [...deliveries];
    if (statusFilter !== 'all') {
      result = result.filter(d => d.status === statusFilter);
    }
    // Show top 20 on dashboard now that charts are gone
    setFilteredDeliveries(result.slice(0, 20));
  };

  const statCards = [
    {
      title: 'Total Orders',
      value: stats.totalOrders,
      icon: TrendingUp,
      color: 'bg-blue-500',
      link: '/orders'
    },
    {
      title: 'Total Revenue',
      value: `$${stats.totalRevenue.toLocaleString()}`,
      icon: DollarSign,
      color: 'bg-green-500',
      link: '/orders'
    },
    {
      title: 'Total Customers',
      value: stats.totalCustomers,
      icon: Users,
      color: 'bg-purple-500',
      link: '/customers'
    },
    {
      title: 'Low Stock Items',
      value: stats.lowStockItems,
      icon: AlertCircle,
      color: 'bg-red-500',
      link: '/inventory'
    },
  ];

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      in_transit: 'bg-blue-100 text-blue-800',
      delivered: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      delayed: 'bg-orange-100 text-orange-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold">Dashboard</h1>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {statCards.map((stat) => {
          const Icon = stat.icon;
          return (
            <Link key={stat.title} to={stat.link}>
              <div className="card hover:shadow-lg transition-shadow cursor-pointer">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-gray-500 text-sm">{stat.title}</p>
                    <p className="text-3xl font-bold mt-2">{stat.value}</p>
                  </div>
                  <div className={`${stat.color} p-3 rounded-lg`}>
                    <Icon className="text-white" size={24} />
                  </div>
                </div>
              </div>
            </Link>
          );
        })}
      </div>

      {/* Delivery Status (Excel Form) - Replaced Charts */}
      <div className="card overflow-hidden p-0 border border-gray-300">
        <div className="p-4 border-b border-gray-300 bg-gray-50 flex flex-col md:flex-row items-center justify-between gap-4">
          <h3 className="text-lg font-semibold">Live Delivery Monitoring</h3>
          <div className="flex items-center gap-2">
            <Filter size={18} className="text-gray-400" />
            <select
              className="input-field py-1"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="in_transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="failed">Failed</option>
              <option value="delayed">Delayed</option>
            </select>
          </div>
        </div>
        
        <div className="overflow-x-auto">
          {filteredDeliveries.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No deliveries found for the selected status.
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-100 border-b border-gray-300">
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Order #</th>
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Country</th>
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Invoice #</th>
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Carrier</th>
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Tracking #</th>
                  <th className="text-left py-2 px-4 border-r border-gray-300 font-bold text-xs uppercase tracking-wider text-gray-600">Last Update</th>
                  <th className="text-left py-2 px-4 font-bold text-xs uppercase tracking-wider text-gray-600">Status</th>
                </tr>
              </thead>
              <tbody>
                {filteredDeliveries.map((delivery) => (
                  <tr key={delivery.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                    <td className="py-1.5 px-4 border-r border-gray-200 font-medium text-sm">
                      <Link to={`/orders/${delivery.order_id}`} className="text-primary-600 hover:underline">
                        #{delivery.order_number}
                      </Link>
                    </td>
                    <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.country}</td>
                    <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.invoice_no}</td>
                    <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-600">{delivery.carrier}</td>
                    <td className="py-1.5 px-4 border-r border-gray-200 text-sm font-mono text-gray-600">{delivery.tracking_number}</td>
                    <td className="py-1.5 px-4 border-r border-gray-200 text-sm text-gray-500 whitespace-nowrap">{delivery.last_update}</td>
                    <td className="py-1.5 px-4 text-sm">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(delivery.status)}`}>
                        {delivery.status?.replace('_', ' ')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="p-4 bg-gray-50 border-t border-gray-300 text-center">
          <Link to="/deliveries" className="btn-secondary inline-block">
            Go to Full Deliveries Manager
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
